import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Link, RouteProps } from "react-router-dom";
import { Form, Button, Divider, Grid, Header, Select, Icon, Label, Input } from "semantic-ui-react";
import { SemanticCOLORS } from "semantic-ui-react/dist/commonjs/generic";
import {
  getPencilerStartProjectsStatus,
  remapRoleNameFromRoleName
} from "../../actions/accounts/accounts";
import { formatDateAsShortDate } from "../../actions/dateTimeFormats/dateTimeFormats";
import { getRegionSettings } from "../../actions/regions/regionSettings";
import { getRegionSettingsPerUser } from "../../actions/regions/regionSettingsPerUser";
import {
  canPerformAction,
  changeOrganizationsViewport,
  changeUserAttribute,
  handleDeleteAccountFormSubmit,
  isExternal
} from "../../actions/users/users";
import { amRoutes, pencilerRoles, urbancanvasRoles } from "../../config/config";
import { messages } from "../../config/constants";
import { AppDispatch, RootState } from "../../reducers";
import { setDeleteAccountModal } from "../../store/users";
import { UserDetail } from "../../types/users";
import { textInputRender } from "../../utils/components";
import {
  validateUsedUser,
  validateEmailString,
  validateUsedEmail,
  validatePassword
} from "../../utils/formValidation";
import { exists, canManageUsers } from "../../utils/utils";
import ButtonLink from "../common/ButtonLink";
import DismissibleMessage from "../common/DismissibleMesage";
import FormModal from "../common/FormModal";
import TableRow from "../common/TableRow";
import TextInputFormModal from "../common/TextInputForm";
import DeleteAccountModal from "./DeleteAccountModal";

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.accounts.organization,
    user: state.users.userClaims,
    userTypes: state.users.userTypes,
    openDeleteAccountModal: state.users.openDeleteAccountModal,
    deleteAccountFailed: state.users.deleteAccountFailed,
    deleteAccountMessage: state.users.deleteAccountMessage,
    showSuccessMessage: state.statusMessages.showSuccessMessage,
    submitSucceededMessageHeader: state.statusMessages.submitSucceededMessageHeader,
    submitSucceededMessageContent: state.statusMessages.submitSucceededMessageContent,
    startProjects: state.accounts.pencilerStartProjectsStatus,
    gettingStartProjects: state.accounts.gettingPencilerStartProjectsStatus,
    getStartProjectsSuccess: state.accounts.getPencilerStartProjectsStatusSuccess,
    getStartProjectsError: state.accounts.getPencilerStartProjectsStatusError,
    getStartProjectsErrorMessage: state.accounts.getPencilerStartProjectsStatusErrorMessage,
    areRegionSettingsLoading: state.regionSettings.loading,
    doRegionSettingsExist: exists(state.regionSettings.id),
    areRegionSettingsPerUserLoading: state.regionSettingsPerUser.loading,
    doRegionSettingsPerUserExist: exists(state.regionSettingsPerUser.userId)
  };
};

const successChangeMessageTemplate = (label: string) => `${label} successfully changed!`;
const failureMessageTemplate = (label: string) => `There was an error changing your ${label}!`;
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  changePassword: (password: string) =>
    dispatch(
      changeUserAttribute(
        "password",
        password,
        successChangeMessageTemplate("Password"),
        failureMessageTemplate("password")
      )
    ),
  //@ts-ignore: unused action
  changeViewport: viewport => dispatch(changeOrganizationsViewport(viewport)),
  changeEmail: (email: string) =>
    dispatch(
      changeUserAttribute(
        "email",
        email,
        successChangeMessageTemplate("Email"),
        failureMessageTemplate("email")
      )
    ),
  changeUsername: (username: string) =>
    dispatch(
      changeUserAttribute(
        "user",
        username,
        successChangeMessageTemplate("Username"),
        failureMessageTemplate("username")
      )
    ),
  changeUserType: (userType: number) =>
    dispatch(
      changeUserAttribute(
        "userTypeId",
        userType,
        successChangeMessageTemplate("User Type"),
        failureMessageTemplate("user type")
      )
    ),
  changeName: (name: string) =>
    dispatch(
      changeUserAttribute(
        "name",
        name,
        successChangeMessageTemplate("Name"),
        failureMessageTemplate("name")
      )
    ),
  changePhone: (details: { phone: string }) =>
    dispatch(
      changeUserAttribute(
        "details",
        details,
        successChangeMessageTemplate("Phone"),
        failureMessageTemplate("phone")
      )
    ),
  changeJobTitle: (details: { jobTitle: string }) =>
    dispatch(
      changeUserAttribute(
        "details",
        details,
        successChangeMessageTemplate("Job Title"),
        failureMessageTemplate("job title")
      )
    ),
  changeAddress: (details: Partial<UserDetail>) =>
    dispatch(
      changeUserAttribute(
        "details",
        details,
        successChangeMessageTemplate("Address"),
        failureMessageTemplate("address")
      )
    ),
  getPencilerStartProjectsStatus: () => dispatch(getPencilerStartProjectsStatus()),
  setDeleteAccountModal: (isOpen: boolean) => dispatch(setDeleteAccountModal(isOpen)),
  handleDeleteAccountFormSubmit: () => dispatch(handleDeleteAccountFormSubmit()),
  remapRoleNameFromRoleName: (roleName: string) => remapRoleNameFromRoleName(roleName),
  canDeleteAccount: () => dispatch(canPerformAction("user", "delete", "own")),
  isExternal: () => dispatch(isExternal()),
  getRegionSettings: () => dispatch(getRegionSettings()),
  getRegionSettingsPerUser: () => dispatch(getRegionSettingsPerUser()),
  formatDateAsShortDate: (date: string) => dispatch(formatDateAsShortDate(date))
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type AccountProps = {} & RouteProps & PropsFromRedux;

class Account extends React.Component<AccountProps> {
  constructor(props: AccountProps) {
    super(props);
    props.getPencilerStartProjectsStatus();
    if (!props.areRegionSettingsLoading && !props.doRegionSettingsExist) {
      props.getRegionSettings();
    }
    if (!props.areRegionSettingsPerUserLoading && !props.doRegionSettingsPerUserExist) {
      props.getRegionSettingsPerUser();
    }
  }
  state = {
    isPasswordVisible: false
  };
  handleDeleteAccountClick = () => this.props.setDeleteAccountModal(true);

  handlePasswordIconClick = () =>
    this.setState({ isPasswordVisible: !this.state.isPasswordVisible });

  _renderRoles() {
    const roleNames = (this.props.user.selectedRoles ?? []).map(role => role.name).join(", ");
    return <TableRow title="Status" value={roleNames} />;
  }

  _canManageSubscription = () =>
    this.props.user.roles?.some(
      role =>
        pencilerRoles.canManageSubscription.includes(role.code) ||
        urbancanvasRoles.canManageSubscription.includes(role.code)
    );

  _canEditViewport = () =>
    this.props.user.roles?.some(role => pencilerRoles.canEditViewport.includes(role.code));

  _unauthorizedManagementMessage = () => {
    if (
      this.props.user.roles?.length === 1 &&
      this.props.user.roles[0].code === pencilerRoles.pencilerDemoCode
    ) {
      return (
        <div style={{ maxWidth: "450px" }}>
          <div>Demo users cannot manage the account or edit viewport.</div>
          <div>
            Purchase a subscription under <i>Subscription and Usage</i> to upgrade your account and
            become an Administrator.
          </div>
        </div>
      );
    }
    return (
      <div>
        <div>You have been assigned the role of Viewer by the account Administrator.</div>
        <div>Viewers cannot manage the account, subscription or edit viewport.</div>
      </div>
    );
  };

  _renderRoleLabels = () => {
    const labels = (this.props.user.selectedRoles?.slice() ?? [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(role => {
        const roleName = this.props.remapRoleNameFromRoleName(role.name);
        let color = pencilerRoles.defaultColor;
        if (Object.keys(pencilerRoles.colors).includes(role.code))
          // @ts-ignore: Safe access
          color = pencilerRoles.colors[role.code];
        return (
          <Label key={role.code} as="a" color={color as SemanticCOLORS} image>
            {roleName}
          </Label>
        );
      });
    return <div>{labels}</div>;
  };

  _renderDeleteAccountRow = () => (
    <Grid.Row
      columns={1}
      style={{ maxWidth: 750, marginTop: "20px", display: "table", padding: 0 }}
    >
      <Divider horizontal>
        <Header as="h4">
          <Icon name="remove user" />
          Delete account
        </Header>
      </Divider>
    </Grid.Row>
  );

  _renderDeleteAccountButtonRow = () => (
    <Grid.Row columns={1} style={{ maxWidth: 750 }}>
      <Button negative onClick={() => this.handleDeleteAccountClick()}>
        Delete account
      </Button>
    </Grid.Row>
  );

  topProfileSection = {
    paddingTop: "1rem",
    paddingBottom: 0
  };
  innerProfileSection = {
    paddingBottom: 0,
    paddingTop: 0
  };
  bottomProfileSection = {
    paddingTop: 0,
    paddingBottom: "1rem"
  };

  render() {
    return (
      <div>
        {!exists(this.props.user.roles) && (
          <DismissibleMessage
            warning
            style={{ textAlign: "center" }}
            content={
              // eslint-disable-next-line max-len
              "Your user account is not associated with any active UrbanSim subscription. Contact support@urbansim.com for more information."
            }
          />
        )}
        {this.props.openDeleteAccountModal && (
          <DeleteAccountModal
            // @ts-ignore
            onModalClose={() => this.props.setDeleteAccountModal(false)}
            openModal={this.props.openDeleteAccountModal}
            formSubmitFailed={this.props.deleteAccountFailed}
            formSubmitFailedMessage={this.props.deleteAccountMessage}
            onSubmit={() => this.props.handleDeleteAccountFormSubmit()}
            isUserOwnerOfAccount={
              this.props.organization.administratorUserId === this.props.user.id
            }
          />
        )}
        <Grid textAlign="center" style={{ height: "100%" }} verticalAlign="top">
          <Grid.Row columns={1} style={{ maxWidth: 750, marginTop: "20px" }}>
            <Grid.Column width={16} textAlign="left">
              <Header as="h2" textAlign="left">
                My Profile
              </Header>
              {this.props.isExternal() && (
                <DismissibleMessage
                  className={messages.hasExternalSubscription.className}
                  content={messages.hasExternalSubscription.content}
                />
              )}
            </Grid.Column>
          </Grid.Row>
          {this.props.user.roles && (
            <Grid.Row columns={2} style={{ maxWidth: 750 }}>
              <Grid.Column width={7} textAlign="left">
                <div>
                  <b> {this.props.user.roles.length > 1 ? "Roles:" : "Role:"} </b>
                </div>
              </Grid.Column>
              <Grid.Column width={9} textAlign="left">
                {this._renderRoleLabels()}
              </Grid.Column>
            </Grid.Row>
          )}
          {this.props.user.roles?.some(role => role.system === pencilerRoles.system) && (
            <TableRow
              title="Account"
              value={this.props.organization.name}
              linkTitle="Manage list"
              content={
                canManageUsers(this.props.user) && <Link to={amRoutes.managementUsers}>Manage</Link>
              }
            />
          )}
          <TableRow
            style={this.topProfileSection}
            title="Name"
            value={<b>{this.props.user.name}</b>}
            content={TextInputFormModal({
              name: "name",
              label: "Name",
              initialValue: this.props.user.name,
              validators: [],
              required: true,
              placeholder: "Type in your new name",
              onSubmit: ({ name }) => this.props.changeName(name),
              disabled: this.props.isExternal()
            })}
          />
          <TableRow
            style={this.innerProfileSection}
            title="Job title"
            value={<b>{this.props.user.details?.jobTitle}</b>}
            content={TextInputFormModal({
              name: "jobTitle",
              label: "Job title",
              initialValue: this.props.user.details?.jobTitle ?? undefined,
              validators: [],
              required: true,
              placeholder: "Type in your job title",
              onSubmit: ({ jobTitle }) => this.props.changeJobTitle({ jobTitle }),
              disabled: this.props.isExternal()
            })}
          />
          <TableRow
            title="Email"
            style={this.innerProfileSection}
            value={<b>{this.props.user.email}</b>}
            content={TextInputFormModal({
              name: "email",
              label: "Email",
              initialValue: this.props.user.email ?? undefined,
              validators: [validateEmailString, validateUsedEmail],
              required: true,
              placeholder: "Type in your new email",
              onSubmit: ({ email }) => this.props.changeEmail(email),
              disabled: this.props.isExternal()
            })}
          />
          <TableRow
            title="Phone"
            style={this.innerProfileSection}
            value={<b>{this.props.user.details?.phone}</b>}
            content={TextInputFormModal({
              name: "phone",
              label: "Phone",
              initialValue: this.props.user.details?.phone ?? undefined,
              validators: [],
              required: false,
              placeholder: "Type in your new phone",
              onSubmit: ({ phone }) => this.props.changePhone({ phone: phone || "" }),
              disabled: this.props.isExternal()
            })}
          />
          <TableRow
            title="Address"
            style={this.bottomProfileSection}
            value={
              <>
                {this.props.user.details?.organization && (
                  <b>{this.props.user.details.organization}</b>
                )}
                {this.props.user.details?.street && (
                  <>
                    <br />
                    {this.props.user.details.street}
                  </>
                )}
                {this.props.user.details?.city && (
                  <>
                    <br />
                    {this.props.user.details.city}
                  </>
                )}
                {this.props.user.details?.state && (
                  <>
                    <br />
                    {this.props.user.details.state}
                  </>
                )}
                {this.props.user.details?.zip && (
                  <>
                    <br />
                    {this.props.user.details.zip}
                  </>
                )}
                {this.props.user.details?.country && (
                  <>
                    <br />
                    {this.props.user.details.country}
                  </>
                )}
              </>
            }
            content={
              <FormModal
                size="mini"
                fields={[
                  {
                    name: "organization",
                    initialValue: this.props.user.details?.organization,
                    required: true,
                    render: textInputRender("Organization", "Type in your organization", true)
                  },
                  {
                    name: "street",
                    initialValue: this.props.user.details?.street,
                    render: textInputRender("Street", "Type in your street address")
                  },
                  {
                    name: "city",
                    initialValue: this.props.user.details?.city,
                    render: textInputRender("City", "Type in your city")
                  },
                  {
                    name: "state",
                    initialValue: this.props.user.details?.state,
                    render: textInputRender("State / Province", "Type in your State / Province")
                  },
                  {
                    name: "zip",
                    initialValue: this.props.user.details?.zip,
                    render: textInputRender("Zip / Postal Code", "Type in your Zip / Postal code")
                  },
                  {
                    name: "country",
                    initialValue: this.props.user.details?.country,
                    render: textInputRender("Country", "Type in your country")
                  }
                ]}
                onSubmit={({ organization, street, city, state, zip, country }) => {
                  const parsedValues = {
                    organization: organization || "",
                    street: street || "",
                    city: city || "",
                    zip: zip || "",
                    state: state || "",
                    country: country || ""
                  };
                  this.props.changeAddress(parsedValues);
                }}
                submitText={"Save"}
                modalHeader={"Change Address"}
                modalTrigger={onClick => (
                  <ButtonLink onClick={onClick} disabled={this.props.isExternal()}>
                    Change
                  </ButtonLink>
                )}
              />
            }
          />
          <TableRow
            style={this.topProfileSection}
            title="Username"
            value={<b>{this.props.user.user}</b>}
            content={TextInputFormModal({
              name: "username",
              label: "Username",
              initialValue: this.props.user.user,
              validators: [validateUsedUser],
              required: true,
              placeholder: "Type in your new username",
              onSubmit: ({ username }) => this.props.changeUsername(username),
              disabled: this.props.isExternal()
            })}
          />
          <TableRow
            style={this.bottomProfileSection}
            title="Password"
            value="********"
            content={
              <FormModal
                onClose={() => this.setState({ isPasswordVisible: false })}
                size="mini"
                fields={[
                  {
                    name: "password",
                    required: true,
                    render: ({ input, meta }) => (
                      <Form.Field
                        iconPosition="left"
                        icon={
                          <Icon
                            link
                            name={this.state.isPasswordVisible ? "eye" : "eye slash"}
                            onClick={this.handlePasswordIconClick}
                          />
                        }
                        error={
                          meta.touched &&
                          meta.error && {
                            content: meta.error,
                            pointing: "below"
                          }
                        }
                        control={Input}
                        required={true}
                        type={this.state.isPasswordVisible ? "text" : "password"}
                        label={"New password"}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                        name={input.name}
                        value={input.value}
                        onBlur={input.onBlur}
                        onFocus={input.onFocus}
                        onChange={input.onChange}
                      />
                    )
                  },
                  {
                    name: "confirm",
                    required: true,
                    render: ({ input, meta }) => (
                      <Form.Field
                        error={
                          !meta.active &&
                          meta.touched &&
                          meta.error && {
                            content: meta.error,
                            pointing: "below"
                          }
                        }
                        iconPosition="left"
                        icon={
                          <Icon
                            link
                            name={this.state.isPasswordVisible ? "eye" : "eye slash"}
                            onClick={this.handlePasswordIconClick}
                          />
                        }
                        type={this.state.isPasswordVisible ? "text" : "password"}
                        required={true}
                        control={Input}
                        label={"Confirm password"}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                        name={input.name}
                        value={input.value}
                        onBlur={input.onBlur}
                        onFocus={input.onFocus}
                        onChange={input.onChange}
                      />
                    )
                  }
                ]}
                onSubmit={({ password }) => this.props.changePassword(password)}
                submitText={"Save"}
                modalHeader={"Change Password"}
                modalTrigger={onClick => (
                  <ButtonLink
                    className="link"
                    style={{ background: "none", border: "none", color: "#4c92df" }}
                    onClick={onClick}
                    disabled={this.props.isExternal()}
                  >
                    Change
                  </ButtonLink>
                )}
                validate={values => {
                  const errors: { password?: string; confirm?: string } = {};
                  if (!values.password) {
                    errors.password = "This field is required";
                  }
                  const errorMessage = validatePassword(values.password);
                  if (exists(errorMessage)) {
                    errors.password = errorMessage;
                  }
                  if (!values.confirm) {
                    errors.confirm = "This field is required";
                  }
                  if (values.password !== values.confirm) {
                    errors.confirm = "Passwords do not match";
                  }
                  return errors;
                }}
              />
            }
          />
          <TableRow
            title="User type"
            value={<b>{this.props.user.userType && this.props.user.userType.name}</b>}
            content={
              <FormModal
                fields={[
                  {
                    name: "userType",
                    required: true,
                    initialValue: this.props.user.userType ? this.props.user.userType.id : null,
                    render: ({ input }) => (
                      <Form.Field
                        aria-label="User type selection"
                        control={Select}
                        label={"User types"}
                        required
                        placeholder="Select type"
                        name={input.name}
                        value={input.value}
                        onBlur={input.onBlur}
                        onFocus={input.onFocus}
                        onChange={(_: any, { value }: any) => input.onChange(value)}
                        options={this.props.userTypes.map(userType => ({
                          key: userType.id,
                          text: userType.name,
                          value: userType.id
                        }))}
                      />
                    )
                  }
                ]}
                onSubmit={({ userType }) => this.props.changeUserType(userType)}
                submitText={"Save"}
                modalHeader={"Change User type"}
                modalTrigger={onClick => (
                  <ButtonLink
                    className="link"
                    style={{
                      background: "none",
                      border: "none",
                      color: "#4c92df"
                    }}
                    onClick={onClick}
                    disabled={this.props.isExternal()}
                  >
                    Change
                  </ButtonLink>
                )}
              />
            }
          />
          <TableRow
            title="User created"
            value={
              <b>
                {this.props.user?.createdAt
                  ? this.props.formatDateAsShortDate(this.props.user?.createdAt)
                  : ""}
              </b>
            }
          />
          <Grid.Row
            columns={1}
            style={{ maxWidth: 750, marginTop: "20px", display: "table", padding: 0 }}
          />
          {this.props.canDeleteAccount() && (
            <>
              {this._renderDeleteAccountRow()}
              {this._renderDeleteAccountButtonRow()}
            </>
          )}
        </Grid>
      </div>
    );
  }
}

export default connector(Account);
